/**
 * 新用户操作引导 - 核心逻辑（多场景版）
 * 基于 driver.js，支持跨页面引导和多场景独立追踪
 *
 * 各场景版本号：修改步骤内容后递增对应版本，用户会重新看到该场景引导
 */
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'

    // 注入全局样式：引导运行时关键导航元素始终在遮罩之上
    ; (function injectGuideStyles() {
        var style = document.createElement('style')
        style.textContent = [
            '/* 引导运行时，编辑器 Tab 栏和顶部导航保持在遮罩之上 */',
            'body.driver-active .editor-topbar { position: relative; z-index: 10001 !important; }',
            'body.driver-active .header-container { position: relative; z-index: 10001 !important; }',
            '/* 解除 overflow:hidden 裁剪，让子元素能突破容器显示在遮罩之上 */',
            'body.driver-active .main-container { overflow: visible !important; }',
            'body.driver-active .right-content-container { overflow: visible !important; }',
            'body.driver-active .form-editor { overflow: visible !important; }'
        ].join('\n')
        document.head.appendChild(style)
    })()

/** 各场景当前版本号 */
export const GUIDE_VERSIONS = {
    bzmj: 2,
    projectList: 1,
    projectDetail: 1,
    formEditor: 2,
    admin: 1
}

/**
 * 检查并启动引导（多场景）
 * @param {Object} user - 用户信息（需要包含 guideData 或 GuideData 字段，JSON 字符串）
 * @param {string} guideKey - 引导场景 key
 * @param {Array} steps - 当前场景的引导步骤
 * @param {Function} markGuideFn - 调用后端标记已阅的函数 (guideKey, version) => void
 * @param {Object} router - Vue Router 实例（跨页面时需要）
 */
// 全局标志：防止引导运行中被重复启动
var _guideRunning = {}

export function checkGuide(user, guideKey, steps, markGuideFn, router) {
    if (!user || !guideKey || !steps || steps.length === 0) return
    // 防止同一场景重复启动
    if (_guideRunning[guideKey]) return

    var currentVersion = GUIDE_VERSIONS[guideKey] || 1

    // 解析用户 guideData
    var guideData = {}
    var raw = user.guideData || user.GuideData || '{}'
    if (typeof raw === 'string') {
        try { guideData = JSON.parse(raw) } catch (e) { guideData = {} }
    } else if (typeof raw === 'object') {
        guideData = raw
    }
    var userVersion = guideData[guideKey] || 0
    if (userVersion >= currentVersion) return

    // 筛选有效步骤
    // 对于带 route 的步骤，检查对应路由的 tab 导航元素是否存在（仅项目详情页有 tab 菜单）
    // 如果用户没有某个菜单权限，tab 不会渲染，该路由下所有步骤应过滤掉
    var hasTabMenu = !!document.querySelector('a.pl-sub-tab') // 当前页面是否有 tab 式菜单
    var availableRoutes = {} // 缓存 route -> 是否可用
    var validSteps = steps.filter(function (s) {
        if (!s.element) return true
        if (s.route) {
            // 只有在有 tab 菜单的页面（如项目详情页）才做权限过滤
            if (!hasTabMenu) return true
            // 检查该 route 是否已缓存
            if (availableRoutes[s.route] !== undefined) return availableRoutes[s.route]
            // 从 route 提取路径末段（如 /project/1/overview → overview）
            var parts = s.route.split('/')
            var lastSeg = parts[parts.length - 1]
            // 检查对应的 tab 导航元素是否在当前页面存在
            var tabEl = document.querySelector('a.pl-sub-tab[href*="/' + lastSeg + '"]')
            availableRoutes[s.route] = !!tabEl
            return !!tabEl
        }
        return document.querySelector(s.element)
    })
    if (validSteps.length === 0) return

    _guideRunning[guideKey] = true

    var guideInstance = driver({
        showProgress: true,
        animate: true,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: 'guide-popover',
        nextBtnText: '下一步',
        prevBtnText: '上一步',
        doneBtnText: '完成',
        progressText: '{{current}} / {{total}}',
        steps: validSteps.map(function (s) {
            return {
                element: s.element,
                popover: s.popover
            }
        }),
        onHighlightStarted: function (element, step, opts) {
            var stepConfig = validSteps[opts.state.activeIndex || 0]
            if (stepConfig && stepConfig.onHighlightStarted) {
                stepConfig.onHighlightStarted()
            }
        },
        onNextClick: function () {
            var activeIndex = guideInstance.getActiveIndex() || 0
            var nextIndex = activeIndex + 1
            if (nextIndex >= validSteps.length) {
                if (markGuideFn) {
                    markGuideFn(guideKey, currentVersion)
                }
                guideInstance.destroy()
                _guideRunning[guideKey] = false
                return
            }

            // 查找下一个有效步骤（跳过 element 不存在的步骤）
            function tryMoveToIndex(targetIndex) {
                if (targetIndex >= validSteps.length) {
                    if (markGuideFn) markGuideFn(guideKey, currentVersion)
                    guideInstance.destroy()
                    _guideRunning[guideKey] = false
                    return
                }
                var targetStep = validSteps[targetIndex]
                var needRoute = targetStep.route && router
                var currentPath = router && router.currentRoute ? router.currentRoute.path : ''
                var needNavigate = needRoute && currentPath !== targetStep.route

                if (needNavigate) {
                    // 立即隐藏遮罩和弹窗，清理高亮样式
                    var overlay = document.querySelector('.driver-overlay')
                    var popover = document.querySelector('.driver-popover')
                    if (overlay) overlay.style.display = 'none'
                    if (popover) popover.style.display = 'none'
                    // 清理被高亮元素的样式，防止 Vue 销毁 DOM 后样式残留
                    var activeEls = document.querySelectorAll('.driver-active-element')
                    for (var j = 0; j < activeEls.length; j++) {
                        activeEls[j].classList.remove('driver-active-element')
                        activeEls[j].style.removeProperty('z-index')
                        activeEls[j].style.removeProperty('position')
                    }
                    router.push({ path: targetStep.route, query: router.currentRoute.query }).catch(function () { })
                }
                var delay = needNavigate ? 800 : 50
                setTimeout(function () {
                    // 恢复遮罩显示
                    if (needNavigate) {
                        var overlay = document.querySelector('.driver-overlay')
                        var popover = document.querySelector('.driver-popover')
                        if (overlay) overlay.style.display = ''
                        if (popover) popover.style.display = ''
                    }
                    // 先执行 onHighlightStarted 回调（可能切换 Tab）
                    if (targetStep.onHighlightStarted) {
                        targetStep.onHighlightStarted()
                    }
                    // 给 DOM 更新的时间（Tab 切换后组件需要渲染）
                    var domDelay = targetStep.onHighlightStarted ? 300 : 0
                    setTimeout(function () {
                        // 检查 element 是否存在
                        if (targetStep.element && !document.querySelector(targetStep.element)) {
                            tryMoveToIndex(targetIndex + 1)
                            return
                        }
                        guideInstance.moveTo(targetIndex)
                    }, domDelay)
                }, delay)
            }
            tryMoveToIndex(nextIndex)
        },
        onPrevClick: function () {
            var activeIndex = guideInstance.getActiveIndex() || 0
            var prevIndex = activeIndex - 1
            if (prevIndex < 0) return

            // 查找上一个有效步骤
            function tryMoveToPrev(targetIndex) {
                if (targetIndex < 0) return
                var targetStep = validSteps[targetIndex]
                var needRoute = targetStep.route && router
                var currentPath = router && router.currentRoute ? router.currentRoute.path : ''
                var needNavigate = needRoute && currentPath !== targetStep.route

                if (needNavigate) {
                    var overlay = document.querySelector('.driver-overlay')
                    var popover = document.querySelector('.driver-popover')
                    if (overlay) overlay.style.display = 'none'
                    if (popover) popover.style.display = 'none'
                    var activeEls = document.querySelectorAll('.driver-active-element')
                    for (var j = 0; j < activeEls.length; j++) {
                        activeEls[j].classList.remove('driver-active-element')
                        activeEls[j].style.removeProperty('z-index')
                        activeEls[j].style.removeProperty('position')
                    }
                    router.push({ path: targetStep.route, query: router.currentRoute.query }).catch(function () { })
                }
                var delay = needNavigate ? 800 : 50
                setTimeout(function () {
                    if (needNavigate) {
                        var overlay = document.querySelector('.driver-overlay')
                        var popover = document.querySelector('.driver-popover')
                        if (overlay) overlay.style.display = ''
                        if (popover) popover.style.display = ''
                    }
                    if (targetStep.onHighlightStarted) {
                        targetStep.onHighlightStarted()
                    }
                    var domDelay = targetStep.onHighlightStarted ? 300 : 0
                    setTimeout(function () {
                        if (targetStep.element && !document.querySelector(targetStep.element)) {
                            tryMoveToPrev(targetIndex - 1)
                            return
                        }
                        guideInstance.moveTo(targetIndex)
                    }, domDelay)
                }, delay)
            }
            tryMoveToPrev(prevIndex)
        },
        onDestroyStarted: function () {
            if (markGuideFn) {
                markGuideFn(guideKey, currentVersion)
            }
            guideInstance.destroy()
            _guideRunning[guideKey] = false
        }
    })

    // 第一步如果有 route 且不在当前页，先跳转
    var firstStep = validSteps[0]
    var currentPath = router && router.currentRoute ? router.currentRoute.path : ''
    if (firstStep && firstStep.route && router && currentPath !== firstStep.route) {
        router.push({ path: firstStep.route, query: router.currentRoute.query }).catch(function () { })
        setTimeout(function () { guideInstance.drive() }, 800)
    } else {
        setTimeout(function () { guideInstance.drive() }, 800)
    }
}
